import {
  Alert,
  Button,
  FormField,
  Heading,
  Icon,
  NumberStepper,
  PageHeader,
  Section,
  Skeleton,
  Stack,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { errorMessage } from '../../../lib/errors';
import { useCreateColony, type Location } from '../../apiary/api';
import { LocationFormDrawer } from '../../apiary/components/LocationFormDrawer';
import { useBusiness } from '../api';
import { BusinessProfileForm } from '../components/BusinessProfileForm';
import { useSession } from '../session';
import styles from './OnboardingPage.module.css';

/**
 * First steps of the owner after setup (WEB-016): the business, the first
 * location and the colonies standing there.
 */
export function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const data = useBusiness();
  const [location, setLocation] = useState<Location | null>(null);
  const canApiary = can('locations.write') && can('colonies.write');
  const step = location ? 3 : params.get('schritt') === '2' && canApiary ? 2 : 1;
  const toStep2 = () => (canApiary ? setParams({ schritt: '2' }) : navigate('/'));

  return (
    <Stack gap={6} className={styles.page}>
      <PageHeader title={t('onboarding.title')} />
      {step === 1 && (
        <Stack gap={4}>
          <StepTitle>{t('onboarding.step')}</StepTitle>
          {data.data ? (
            <BusinessProfileForm
              business={data.data}
              sections={['basics', 'address', 'contact']}
              submitLabel={canApiary ? t('onboarding.next') : t('onboarding.save')}
              extraActions={
                <Button variant="ghost" onClick={toStep2}>
                  {t('onboarding.skip')}
                </Button>
              }
              onSaved={() => {
                toast.show(t('common.saved'));
                toStep2();
              }}
            />
          ) : (
            <div role="status" aria-label={t('common.loading')}>
              <Skeleton lines={4} />
            </div>
          )}
        </Stack>
      )}
      {step === 2 && <LocationStep onCreated={setLocation} />}
      {step === 3 && location && <ColoniesStep location={location} />}
    </Stack>
  );
}

function StepTitle({ children }: { children: string }) {
  return (
    <Heading level={2} size="lg">
      {children}
    </Heading>
  );
}

function LocationStep({ onCreated }: { onCreated: (l: Location) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  return (
    <Stack gap={4}>
      <StepTitle>{t('onboarding.stepLocation')}</StepTitle>
      <Stack direction="row" gap={2} wrap>
        <Button iconStart={<Icon name="plus" size="sm" />} onClick={() => setOpen(true)}>
          {t('apiary.locations.new')}
        </Button>
        <Button variant="ghost" onClick={() => navigate('/')}>
          {t('onboarding.skip')}
        </Button>
      </Stack>
      {open && <LocationFormDrawer open onClose={() => setOpen(false)} onCreated={onCreated} />}
    </Stack>
  );
}

function ColoniesStep({ location }: { location: Location }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateColony();
  const [count, setCount] = useState<number | null>(3);
  const [year, setYear] = useState<number | null>(new Date().getFullYear() - 1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const labels = { decrement: t('common.decrease'), increment: t('common.increase') };

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      for (let i = 0; i < (count ?? 0); i += 1) {
        await create.mutateAsync({
          location_id: location.id,
          origin_date: `${year ?? new Date().getFullYear()}-01-01`,
          origin_precision: 'year',
        });
      }
      toast.show(t('onboarding.coloniesCreated', { count: count ?? 0 }));
      navigate('/voelker');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap={4}>
      <StepTitle>{t('onboarding.stepColonies')}</StepTitle>
      <Section
        variant="content"
        footer={t('onboarding.stepColoniesText', { location: location.name })}
      >
        <div className={styles.fields}>
          <FormField label={t('onboarding.colonyCount')}>
            {(p) => (
              <NumberStepper
                {...p}
                value={count}
                onChange={setCount}
                min={1}
                max={50}
                labels={labels}
              />
            )}
          </FormField>
          <FormField label={t('onboarding.originYear')}>
            {(p) => (
              <NumberStepper
                {...p}
                value={year}
                onChange={setYear}
                min={1990}
                max={new Date().getFullYear()}
                labels={labels}
              />
            )}
          </FormField>
        </div>
      </Section>
      {error && <Alert tone="danger">{error}</Alert>}
      <Stack direction="row" gap={2} wrap>
        <Button loading={busy} disabled={!count || !year} onClick={() => void submit()}>
          {t('onboarding.createColonies')}
        </Button>
        <Button variant="ghost" onClick={() => navigate('/')}>
          {t('onboarding.skip')}
        </Button>
      </Stack>
    </Stack>
  );
}
